import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { toWebp } from "@/lib/image-webp";

type LibFile = {
  name: string;
  path: string;
  url: string;
  size: number;
  created_at: string | null;
};

const FOLDER = "mailing-library";

export function MailingMediaLibrary() {
  const [userId, setUserId] = useState<string | null>(null);
  const [files, setFiles] = useState<LibFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const load = async (uid: string) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.storage
        .from("vendor-assets")
        .list(`${uid}/${FOLDER}`, {
          limit: 200,
          sortBy: { column: "created_at", order: "desc" },
        });
      if (error) throw error;
      const rows: LibFile[] = (data ?? [])
        .filter((f) => f.name && !f.name.startsWith("."))
        .map((f) => {
          const path = `${uid}/${FOLDER}/${f.name}`;
          const { data: pub } = supabase.storage.from("vendor-assets").getPublicUrl(path);
          return {
            name: f.name,
            path,
            url: pub.publicUrl,
            size: (f.metadata as any)?.size ?? 0,
            created_at: (f as any).created_at ?? null,
          };
        });
      setFiles(rows);
    } catch (err) {
      toast.error((err as Error).message ?? "Knjižnice ni bilo mogoče naložiti.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setLoading(false);
        return;
      }
      setUserId(user.id);
      await load(user.id);
    })();
  }, []);

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const list = e.target.files ? Array.from(e.target.files) : [];
    e.target.value = "";
    if (!userId || list.length === 0) return;
    setUploading(true);
    let ok = 0;
    try {
      for (const file of list) {
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name}: prevelika (max 10 MB).`);
          continue;
        }
        let upload: Blob = file;
        let ext = (file.name.split(".").pop() || "jpg").toLowerCase();
        let contentType = file.type || "image/jpeg";
        try {
          const webp = await toWebp(file, { maxDim: 1600, quality: 0.85 });
          if (webp) {
            upload = webp;
            ext = "webp";
            contentType = "image/webp";
          }
        } catch { /* ignore */ }
        const base = (file.name.replace(/\.[^.]+$/, "") || "slika")
          .toLowerCase()
          .normalize("NFKD")
          .replace(/[\u0300-\u036f]/g, "")
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "")
          .slice(0, 40) || "slika";
        const path = `${userId}/${FOLDER}/${Date.now()}-${base}.${ext}`;
        const { error } = await supabase.storage
          .from("vendor-assets")
          .upload(path, upload, { cacheControl: "3600", upsert: false, contentType });
        if (error) {
          toast.error(`${file.name}: ${error.message}`);
          continue;
        }
        ok++;
      }
      if (ok > 0) toast.success(`Naloženo (${ok}).`);
      await load(userId);
    } finally {
      setUploading(false);
    }
  };


  const copyUrl = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Povezava kopirana.");
    } catch {
      toast.error("Kopiranje ni uspelo.");
    }
  };

  const remove = async (f: LibFile) => {
    if (!userId) return;
    if (!window.confirm(`Izbrisati "${f.name}"?`)) return;
    const { error } = await supabase.storage.from("vendor-assets").remove([f.path]);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Izbrisano.");
    setFiles((cur) => cur.filter((x) => x.path !== f.path));
  };

  return (
    <div className="border border-border p-6 space-y-3">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-serif text-2xl">Knjižnica slik za kampanije</h2>
          <p className="text-sm text-muted-foreground">
            Naloži slike tukaj in jih kopirano povezavo (URL) uporabi v kampanijah (npr. v polju
            HTML vsebine s značko <code className="font-mono text-xs">&lt;img src="…"&gt;</code>).
            Slike se samodejno pretvorijo v WebP.
          </p>
        </div>
        <div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={onUpload}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading || !userId}
            className="bg-accent text-accent-foreground px-4 py-2 text-sm hover:opacity-90 disabled:opacity-50"
          >
            {uploading ? "Nalagam…" : "+ Naloži slike"}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Nalagam…</p>
      ) : files.length === 0 ? (
        <p className="text-sm text-muted-foreground">Knjižnica je prazna. Naloži prvo sliko.</p>
      ) : (
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {files.map((f) => (
            <div key={f.path} className="border border-border bg-background overflow-hidden flex flex-col">
              <div className="aspect-square bg-muted/30 overflow-hidden">
                <img src={f.url} alt={f.name} className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="p-2 space-y-1.5">
                <div className="text-[11px] text-muted-foreground truncate" title={f.name}>{f.name}</div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => copyUrl(f.url)}
                    className="flex-1 border border-border px-2 py-1 text-[11px] hover:bg-muted"
                  >
                    Kopiraj URL
                  </button>
                  <button
                    type="button"
                    onClick={() => remove(f)}
                    className="border border-border px-2 py-1 text-[11px] hover:bg-destructive hover:text-destructive-foreground"
                    aria-label="Izbriši"
                  >
                    ✕
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
